import { ThemeName, themeConfigs } from '@/hero';

export type ThemeColor = { group: string; label: string; value: string; hex: string };

const shades = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'] as const;

type ColorEntry = string | Record<string, string>;

function getHex(entry: ColorEntry): string {
  if (typeof entry === 'string') return entry;
  return entry.DEFAULT ?? '';
}

function buildColorGroup(
  group: string,
  defaultHex: string,
  palette: Record<string, string>,
): ThemeColor[] {
  return [
    { group, label: `${group}`, value: `${group.toLowerCase()}`, hex: defaultHex },
    ...shades.map(s => ({
      group,
      label: `${group} ${s}`,
      value: `${group.toLowerCase()}-${s}`,
      hex: palette[s],
    })),
  ];
}

function buildThemeColors(colors: Record<string, ColorEntry>): ThemeColor[] {
  const paletteKeys: Array<{ group: string; key: string }> = [
    { group: 'Primary', key: 'primary' },
    { group: 'Secondary', key: 'secondary' },
    { group: 'Default', key: 'default' },
    { group: 'Success', key: 'success' },
    { group: 'Warning', key: 'warning' },
    { group: 'Danger', key: 'danger' },
    { group: 'Foreground', key: 'foreground' },
  ];

  const result: ThemeColor[] = [];
  for (const { group, key } of paletteKeys) {
    const entry = colors[key];
    if (typeof entry === 'object') {
      result.push(...buildColorGroup(group, getHex(entry), entry as Record<string, string>));
    }
  }

  const utilityItems: Array<{ label: string; value: string; key: string }> = [
    { label: 'Background', value: 'background', key: 'background' },
    { label: 'Content 1', value: 'content1', key: 'content1' },
    { label: 'Content 2', value: 'content2', key: 'content2' },
    { label: 'Content 3', value: 'content3', key: 'content3' },
    { label: 'Content 4', value: 'content4', key: 'content4' },
    { label: 'Focus', value: 'focus', key: 'focus' },
    { label: 'Divider', value: 'divider', key: 'divider' },
  ];

  for (const item of utilityItems) {
    const entry = colors[item.key];
    if (entry) {
      result.push({ group: 'Utility', label: item.label, value: item.value, hex: getHex(entry) });
    }
  }

  return result;
}

const themeColorCache = new Map<string, ThemeColor[]>();
const tokenToHexCache = new Map<string, Map<string, string>>();

export function getThemeColors(theme: ThemeName = 'bndk'): ThemeColor[] {
  const cached = themeColorCache.get(theme);
  if (cached) return cached;
  const config = themeConfigs[theme];
  const colors = buildThemeColors(config.colors as unknown as Record<string, ColorEntry>);
  themeColorCache.set(theme, colors);
  return colors;
}

function getTokenMap(theme: ThemeName): Map<string, string> {
  const cached = tokenToHexCache.get(theme);
  if (cached) return cached;
  const map = new Map(getThemeColors(theme).map(c => [c.value, c.hex]));
  tokenToHexCache.set(theme, map);
  return map;
}

/** Default THEME_COLORS for backward compatibility (bndk) */
export const THEME_COLORS: ThemeColor[] = getThemeColors('bndk');

/**
 * Resolves a color token name (e.g. "primary-500") to its hex value.
 * If the value is already a hex/CSS color, returns it as-is.
 */
export function resolveThemeColor(
  value: string | undefined | null,
  theme: ThemeName = 'bndk',
): string {
  if (!value) return '';
  return getTokenMap(theme).get(value) ?? value;
}
